import { create } from '@bufbuild/protobuf'
import { timestampFromDate, type Timestamp } from '@bufbuild/protobuf/wkt'
import { createConnectQueryKey, useMutation, useQuery, useTransport } from '@connectrpc/connect-query'
import { useQueryClient } from '@tanstack/react-query'
import { useStore } from '@tanstack/react-store'
import { useCallback, useMemo } from 'react'

import {
  getEmailThread,
  linkEmailThread,
  listEmailThreadEntityCandidates,
  listEmailThreadLinks,
  listEmailThreads,
  listEmailThreadsByEntity,
  markEmailThreadRead,
  unlinkEmailThread,
  updateEmailThread,
} from '@/gen/cortado/v1/cortado-CortadoService_connectquery'
import {
  LinkEmailThreadRequestSchema,
  ListEmailThreadEntityCandidatesRequestSchema,
  ListEmailThreadLinksRequestSchema,
  ListEmailThreadsByEntityRequestSchema,
  MarkEmailThreadReadRequestSchema,
  UnlinkEmailThreadRequestSchema,
} from '@/gen/cortado/v1/email_conversations_pb'
import {
  GetEmailThreadRequestSchema,
  GetEmailThreadResponseSchema,
  ListEmailThreadsRequestSchema,
  UpdateEmailThreadRequestSchema,
  type EmailInboxView,
  type EmailThreadStatus,
  type GetEmailThreadResponse,
  type ListEmailThreadsResponse,
} from '@/gen/cortado/v1/emails_pb'
import { ParentTypeEnum } from '@/gen/cortado/v1/settings_pb'
import {
  findCachedEmailThreadLite,
  getOrganizationIdFromQueryKey,
  invalidateEmailThreadLinkCaches,
  updateCachedEmailThreadLists,
} from './email-thread-cache'
import { organizationStore } from './organization.store'

export interface UseListEmailThreadsOptions {
  /** Filter by thread statuses (e.g., OPEN, CLOSED, NEEDS_REVIEW) */
  statuses?: EmailThreadStatus[]
  /** Filter by assigned user ID */
  assignedTo?: string
  /** Filter by linked inquiry ID */
  inquiryId?: string
  /** Server-backed full-text search across thread subject, participants, and body */
  searchQuery?: string
  /** Inbox tab; naming one also returns every tab's size (viewCounts) */
  view?: EmailInboxView
  /** Only threads that came in on this connected mailbox */
  connectedMailboxId?: string
  /** Only threads unread by the current user */
  unreadOnly?: boolean
  /** Limit on number of threads to return (default: 50) */
  limit?: number
  /** Offset for pagination (default: 0) */
  offset?: number
  /** Field to sort by (default: last_message_at) */
  sortBy?: string
  /** Sort order: "asc" or "desc" (default: desc) */
  sortOrder?: 'asc' | 'desc'
  /** Whether the query is enabled (default: true) */
  enabled?: boolean
  /** Refetch interval in milliseconds, or false to disable polling (default: false — polling disabled) */
  refetchInterval?: number | false
}

/**
 * Hook to fetch email threads for the inbox view
 *
 * This hook:
 * - Fetches email threads for the selected organization
 * - Supports filtering by status, assigned user, and linked inquiry
 * - Supports opt-in polling, but leaves it disabled by default to avoid the
 *   inbox rate-limit incident; mutations and explicit invalidation refresh it
 * - Returns lightweight thread data optimized for list views
 *
 * @param options - Filter and pagination options
 * @returns Email threads with loading, error, and pagination states
 */
export function useListEmailThreads(options: UseListEmailThreadsOptions = {}) {
  const selectedOrganizationId = useStore(organizationStore, (state) => state.selectedOrganizationId)

  const request = useMemo(() => {
    return create(ListEmailThreadsRequestSchema, {
      organizationId: selectedOrganizationId || '',
      statuses: options.statuses || [],
      assignedTo: options.assignedTo,
      inquiryId: options.inquiryId,
      searchQuery: options.searchQuery?.trim() || '',
      view: options.view,
      connectedMailboxId: options.connectedMailboxId || undefined,
      unreadOnly: options.unreadOnly ?? false,
      limit: options.limit,
      offset: options.offset,
      sortBy: options.sortBy,
      sortOrder: options.sortOrder,
    })
  }, [
    selectedOrganizationId,
    options.statuses,
    options.assignedTo,
    options.inquiryId,
    options.searchQuery,
    options.view,
    options.connectedMailboxId,
    options.unreadOnly,
    options.limit,
    options.offset,
    options.sortBy,
    options.sortOrder,
  ])

  const query = useQuery(listEmailThreads, request, {
    enabled: options.enabled !== false && !!selectedOrganizationId,
    staleTime: 30 * 1000, // 30 seconds
    placeholderData: (previousData, previousQuery) => {
      const previousOrganizationId = getOrganizationIdFromQueryKey(previousQuery?.queryKey)
      return previousOrganizationId === selectedOrganizationId ? previousData : undefined
    },
    refetchInterval: options.refetchInterval ?? false, // Polling disabled — see 429 incident; rely on mount/mutation invalidation
  })

  return {
    threads: query.data?.threads ?? [],
    totalCount: query.data?.totalCount ?? 0,
    unreadCount: query.data?.unreadCount ?? 0,
    viewCounts: query.data?.viewCounts,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,
    hasLoaded: query.isSuccess || !!query.data,
    isFetching: query.isFetching,
  }
}

export interface UseEmailThreadsByEntityOptions {
  limit?: number
  offset?: number
  enabled?: boolean
}

/** Fetch conversations linked to any supported CRM entity. */
export function useEmailThreadsByEntity(
  entityType: ParentTypeEnum,
  entityId: string,
  options: UseEmailThreadsByEntityOptions = {},
) {
  const selectedOrganizationId = useStore(organizationStore, (state) => state.selectedOrganizationId)
  const request = useMemo(
    () =>
      create(ListEmailThreadsByEntityRequestSchema, {
        organizationId: selectedOrganizationId || '',
        entityType,
        entityId,
        limit: options.limit,
        offset: options.offset,
      }),
    [selectedOrganizationId, entityType, entityId, options.limit, options.offset],
  )

  const query = useQuery(listEmailThreadsByEntity, request, {
    enabled: options.enabled !== false && !!selectedOrganizationId && !!entityId,
    staleTime: 30 * 1000,
  })

  return {
    threads: query.data?.threads ?? [],
    totalCount: query.data?.totalCount ?? 0,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,
  }
}

/**
 * List every CRM entity a conversation is linked to via the polymorphic
 * `email_thread_links` table. A thread can carry several links at once
 * (e.g. a Company and a Contact simultaneously) — this returns all of them.
 */
export function useEmailThreadLinks(threadId: string, options: { enabled?: boolean } = {}) {
  const selectedOrganizationId = useStore(organizationStore, (state) => state.selectedOrganizationId)
  const request = useMemo(
    () =>
      create(ListEmailThreadLinksRequestSchema, {
        organizationId: selectedOrganizationId || '',
        threadId,
      }),
    [selectedOrganizationId, threadId],
  )

  const query = useQuery(listEmailThreadLinks, request, {
    enabled: options.enabled !== false && !!selectedOrganizationId && !!threadId,
    staleTime: 30 * 1000,
  })

  return {
    links: query.data?.links ?? [],
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,
    hasLoaded: query.isSuccess || !!query.data,
  }
}

/**
 * The open leads and bookings a conversation's contact has, for the "Which
 * event is this about?" picker. Computed by the server on each call, so a lead
 * that was booked or lost since the mail arrived drops out on its own.
 */
export function useEmailThreadEntityCandidates(threadId: string, options: { enabled?: boolean } = {}) {
  const selectedOrganizationId = useStore(organizationStore, (state) => state.selectedOrganizationId)
  const request = useMemo(
    () =>
      create(ListEmailThreadEntityCandidatesRequestSchema, {
        organizationId: selectedOrganizationId || '',
        threadId,
      }),
    [selectedOrganizationId, threadId],
  )

  const query = useQuery(listEmailThreadEntityCandidates, request, {
    enabled: options.enabled !== false && !!selectedOrganizationId && !!threadId,
    staleTime: 30 * 1000,
  })

  return {
    candidates: query.data?.candidates ?? [],
    reviewReason: query.data?.reviewReason,
    isLoading: query.isLoading,
    hasLoaded: query.isSuccess || !!query.data,
  }
}

/** Advance the current user's read position for a conversation. */
export function useMarkEmailThreadRead() {
  const queryClient = useQueryClient()
  const transport = useTransport()
  const mutation = useMutation(markEmailThreadRead, {
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: createConnectQueryKey({
          schema: listEmailThreads,
          transport,
          cardinality: undefined,
        }),
      })
    },
  })

  const markRead = useCallback(
    (organizationId: string, threadId: string, readThrough: Timestamp) =>
      mutation.mutateAsync(
        create(MarkEmailThreadReadRequestSchema, {
          organizationId,
          threadId,
          readThrough,
        }),
      ),
    [mutation.mutateAsync],
  )

  return {
    markRead,
    unreadCount: mutation.data?.unreadCount,
    isPending: mutation.isPending,
  }
}

/**
 * Options for useGetEmailThread hook
 */
export interface UseGetEmailThreadOptions {
  /** Whether the query is enabled (default: true) */
  enabled?: boolean
  /** Refetch interval in milliseconds (default: undefined - no polling) */
  refetchInterval?: number
}

/**
 * Hook to fetch a single email thread by ID with all messages
 *
 * This hook:
 * - Fetches a complete email thread with all messages
 * - Messages are returned in chronological order (oldest first)
 * - Optionally supports polling for real-time updates
 *
 * @param threadId - The ID of the thread to fetch
 * @param options - Query options (enabled, refetchInterval)
 * @returns Email thread with messages, loading, and error states
 */
export function useGetEmailThread(threadId: string, options: UseGetEmailThreadOptions = {}) {
  const request = useMemo(() => {
    return create(GetEmailThreadRequestSchema, { id: threadId })
  }, [threadId])

  const query = useQuery(getEmailThread, request, {
    enabled: options.enabled !== false && !!threadId,
    staleTime: 10 * 1000, // 10 seconds
    refetchInterval: options.refetchInterval,
  })

  return {
    thread: query.data?.thread,
    currentUserDraft: query.data?.currentUserEmailDraft,
    messages: query.data?.thread?.messages ?? [],
    messageCount: query.data?.thread?.messageCount ?? 0,
    subject: query.data?.thread?.subject,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,
    hasLoaded: query.isSuccess || !!query.data,
    isFetching: query.isFetching,
  }
}

/**
 * Hook to update an email thread
 *
 * This hook:
 * - Provides mutation function to update thread properties via the UpdateEmailThread RPC
 * - Automatically invalidates list and detail queries on success
 * - Supports updating status, assigned user, contact, and inquiry links
 *
 * @returns Mutation result with updateThread helper and loading/error states
 */
export function useUpdateEmailThread() {
  const queryClient = useQueryClient()
  const transport = useTransport()

  const mutation = useMutation(updateEmailThread, {
    onSuccess: (response, variables) => {
      const listQueryKey = createConnectQueryKey({
        schema: listEmailThreads,
        transport,
        cardinality: undefined,
      })

      if (response.thread) {
        const listEntries = queryClient.getQueriesData<ListEmailThreadsResponse>({
          queryKey: listQueryKey,
        })
        const previousThread = findCachedEmailThreadLite(listEntries, response.thread.id)
        updateCachedEmailThreadLists(queryClient, listEntries, previousThread, response.thread)

        const detailQueryKey = createConnectQueryKey({
          schema: getEmailThread,
          transport,
          input: create(GetEmailThreadRequestSchema, {
            id: response.thread.id,
          }),
          cardinality: 'finite',
        })

        queryClient.setQueryData<GetEmailThreadResponse>(detailQueryKey, (existing) =>
          create(GetEmailThreadResponseSchema, {
            thread: response.thread,
            currentUserDraft: existing?.currentUserDraft,
            currentUserEmailDraft: existing?.currentUserEmailDraft,
          }),
        )
      }

      queryClient.invalidateQueries({ queryKey: listQueryKey })

      if (variables.id) {
        queryClient.invalidateQueries({
          queryKey: createConnectQueryKey({
            schema: getEmailThread,
            transport,
            input: create(GetEmailThreadRequestSchema, { id: variables.id }),
            cardinality: 'finite',
          }),
        })
      }

      // Any entity's Conversations panel could currently be showing this
      // thread's status/assignment badge.
      queryClient.invalidateQueries({
        queryKey: createConnectQueryKey({
          schema: listEmailThreadsByEntity,
          transport,
          cardinality: undefined,
        }),
      })
    },
  })

  /**
   * Update an email thread's properties
   *
   * @param params - Update parameters
   * @param params.id - The thread ID to update
   * @param params.status - Optional new status (OPEN, CLOSED)
   * @param params.assignedTo - Optional user ID to assign (empty string to unassign)
   * @param params.contactId - Optional contact ID to link (empty string to unlink)
   * @param params.inquiryId - Optional inquiry ID to link (empty string to unlink)
   * @param params.snoozedUntil - Optional date/time to reopen a snoozed thread
   * @param params.muted - Optional mute or unmute, for the current user only
   * @param params.updateMask - Fields to update (paths like "status", "assigned_to")
   */
  const updateThread = async (params: {
    id: string
    status?: EmailThreadStatus
    assignedTo?: string
    contactId?: string
    inquiryId?: string
    snoozedUntil?: Date
    muted?: boolean
    updateMask?: string[]
  }) => {
    const request = create(UpdateEmailThreadRequestSchema, {
      id: params.id,
      status: params.status,
      assignedTo: params.assignedTo,
      contactId: params.contactId,
      inquiryId: params.inquiryId,
      snoozedUntil: params.snoozedUntil ? timestampFromDate(params.snoozedUntil) : undefined,
      muted: params.muted,
      updateMask: params.updateMask ? { paths: params.updateMask } : undefined,
    })
    return mutation.mutateAsync(request)
  }

  return {
    mutation,
    updateThread,
    isLoading: mutation.isPending,
    isError: mutation.isError,
    error: mutation.error,
  }
}

/**
 * Hook to link a conversation to a CRM entity via the polymorphic
 * `email_thread_links` table. A thread may hold several links at once — this
 * adds an edge, it does not replace existing links.
 */
export function useLinkEmailThread() {
  const queryClient = useQueryClient()
  const transport = useTransport()
  const selectedOrganizationId = useStore(organizationStore, (state) => state.selectedOrganizationId)

  const mutation = useMutation(linkEmailThread, {
    onSuccess: (_response, variables) => invalidateEmailThreadLinkCaches(queryClient, transport, variables),
  })

  const link = useCallback(
    async (params: { threadId: string; entityType: ParentTypeEnum; entityId: string; organizationId?: string }) => {
      const organizationId = params.organizationId || selectedOrganizationId || ''
      const request = create(LinkEmailThreadRequestSchema, {
        organizationId,
        threadId: params.threadId,
        entityType: params.entityType,
        entityId: params.entityId,
      })
      return mutation.mutateAsync(request)
    },
    [mutation.mutateAsync, selectedOrganizationId],
  )

  return {
    mutation,
    link,
    isLoading: mutation.isPending,
    isError: mutation.isError,
    error: mutation.error,
  }
}

/** Hook to remove one entity link from a conversation. */
export function useUnlinkEmailThread() {
  const queryClient = useQueryClient()
  const transport = useTransport()
  const selectedOrganizationId = useStore(organizationStore, (state) => state.selectedOrganizationId)

  const mutation = useMutation(unlinkEmailThread, {
    onSuccess: (_response, variables) => invalidateEmailThreadLinkCaches(queryClient, transport, variables),
  })

  const unlink = useCallback(
    async (params: { threadId: string; entityType: ParentTypeEnum; entityId: string; organizationId?: string }) => {
      const organizationId = params.organizationId || selectedOrganizationId || ''
      const request = create(UnlinkEmailThreadRequestSchema, {
        organizationId,
        threadId: params.threadId,
        entityType: params.entityType,
        entityId: params.entityId,
      })
      return mutation.mutateAsync(request)
    },
    [mutation.mutateAsync, selectedOrganizationId],
  )

  return {
    mutation,
    unlink,
    isLoading: mutation.isPending,
    isError: mutation.isError,
    error: mutation.error,
  }
}
